import { Language } from '../types/kundali';

export type NakshatraMotionType = 'chara' | 'sthira' | 'dvisvabhava' | 'kshipra' | 'ugra' | 'tikshna';

export interface NakshatraClassification {
  index: number; // 0 to 26
  name: string;
  nameHi: string;
  ruler: string;
  deity: string;
  deityHi: string;
  motionType: NakshatraMotionType;
  motionLabelEn: string;
  motionLabelHi: string;
  classicalQualityEn: string;
  classicalQualityHi: string;
  meaningEn: string;
  meaningHi: string;
  bestUsedForEn: string;
  bestUsedForHi: string;
  avoidForEn: string;
  avoidForHi: string;
  sensitiveRole?: {
    roleId: 'janma' | 'karma' | 'samudayika' | 'sanghatika' | 'vinashika' | 'manasa' | 'rajya' | 'abhisheka';
    titleEn: string;
    titleHi: string;
    significanceEn: string;
    significanceHi: string;
  };
}

// 27 Nakshatras complete motion classifications based on Muhurta Chintamani & Brihat Samhita
export const NAKSHATRA_CLASSIFICATIONS: NakshatraClassification[] = [
  {
    index: 0,
    name: 'Ashwini',
    nameHi: 'अश्विनी',
    ruler: 'Ketu',
    deity: 'Ashwini Kumaras (Divine Healers)',
    deityHi: 'अश्विनी कुमार (देव वैद्य)',
    motionType: 'kshipra',
    motionLabelEn: 'Kshipra / Laghu (Swift & Light)',
    motionLabelHi: 'क्षिप्र / लघु (द्रुतगामी)',
    classicalQualityEn: 'Swift, energizing, rejuvenating and initiating.',
    classicalQualityHi: 'शीघ्र फलदायी, ऊर्जावान, आरोग्यवर्धक एवं आरंभिक शक्ति।',
    meaningEn: 'Rapid movement, healing therapies, travel, and energetic new starts.',
    meaningHi: 'तीव्र गति, औषधोपचार, नवीन कार्य आरंभ, तकनीकी व्यापार व यात्रा।',
    bestUsedForEn: 'Administering medicine, buying vehicles, beginning journeys, opening bank accounts, sports & arts.',
    bestUsedForHi: 'दवा सेवन/आरंभ, वाहन क्रय, यात्रा, दुकान/खाता आरंभ, खेल व कलात्मक कार्य।',
    avoidForEn: 'Tasks requiring long-term immobility or patience.',
    avoidForHi: 'अत्यंत दीर्घकालिक व स्थिर प्रकृति के कार्य।'
  },
  {
    index: 1,
    name: 'Bharani',
    nameHi: 'भरणी',
    ruler: 'Venus',
    deity: 'Yama (God of Dharma & Transition)',
    deityHi: 'यमराज (धर्म व रूपांतरण)',
    motionType: 'ugra',
    motionLabelEn: 'Ugra / Krura (Fierce & Forceful)',
    motionLabelHi: 'उग्र / क्रूर (कठोर व उग्र)',
    classicalQualityEn: 'Intense, transformative, decisive and purifying through fire.',
    classicalQualityHi: 'तीक्ष्ण, उग्र, रूपांतरणकारी तथा कटु निर्णयों के लिए अनुकूल।',
    meaningEn: 'Detoxification, clearing debris, restructuring, confronting harsh realities.',
    meaningHi: 'शोधन, पुराने का त्याग, कठोर निर्णय, विध्वंस एवं पुनर्निर्माण।',
    bestUsedForEn: 'Demolition, clearing old debts, surgery, pest control, aggressive debates, forensic research.',
    bestUsedForHi: 'पुरानी संरचना गिराना, शल्य चिकित्सा, ऋण मुक्ति, शत्रु दमन, शोध कार्य।',
    avoidForEn: 'Auspicious celebrations, marriage, Griha Pravesh, gentle social diplomacy.',
    avoidForHi: 'विवाह, गृह प्रवेश, शांति कर्म व कोमल मांगलिक उत्सव।'
  },
  {
    index: 2,
    name: 'Krittika',
    nameHi: 'कृत्तिका',
    ruler: 'Sun',
    deity: 'Agni (God of Sacred Fire)',
    deityHi: 'अग्नि देव (पावक तेज)',
    motionType: 'dvisvabhava',
    motionLabelEn: 'Misra / Dvisvabhava (Mixed & Sharp-Soft)',
    motionLabelHi: 'मिश्र / द्वि-स्वभाव (साधारण व तेज)',
    classicalQualityEn: 'Burning away impurities, analytical precision, dual focus.',
    classicalQualityHi: 'अग्नि तत्व, दोष भस्म करना, तीक्ष्ण विश्लेषण व दोहरा स्वभाव।',
    meaningEn: 'Cooking, metallurgy, purification ceremonies, aggressive negotiations, technical tests.',
    meaningHi: 'हवन-यज्ञ, धातु कर्म, पाक कला, तीक्ष्ण बहस, परीक्षण व शोध।',
    bestUsedForEn: 'Homa/Havan, purchasing gold/metals, taking bold corrective steps, analytical problem solving.',
    bestUsedForHi: 'यज्ञ-अनुष्ठान, सोना/धातु क्रय, सुधारात्मक कठोर निर्णय, परीक्षण।',
    avoidForEn: 'Peace treaties, travel, long investments requiring absolute calm.',
    avoidForHi: 'यात्रा, शांति समझौते व शांत स्वभाव वाले मांगलिक कार्य।'
  },
  {
    index: 3,
    name: 'Rohini',
    nameHi: 'रोहिणी',
    ruler: 'Moon',
    deity: 'Brahma / Prajapati (Creator of Cosmos)',
    deityHi: 'ब्रह्मा / प्रजापति (सृष्टिकर्ता)',
    motionType: 'sthira',
    motionLabelEn: 'Sthira / Achala (Fixed & Permanent)',
    motionLabelHi: 'स्थिर / अचल (चिरस्थायी)',
    classicalQualityEn: 'Fertile, nourishing, enduring, beautiful and exceedingly auspicious.',
    classicalQualityHi: 'अत्यंत शुभ, पोषणकारी, सौंदर्यप्रद, उर्वर एवं स्थायी फलदायी।',
    meaningEn: 'Long-lasting foundations, wealth accumulation, artistic pursuits, relationships.',
    meaningHi: 'दीर्घकालिक आधार, धन संचय, गृह निर्माण, विवाह व कला।',
    bestUsedForEn: 'Griha Pravesh, marriage, laying foundation stones, long-term investments, planting gardens, coronations.',
    bestUsedForHi: 'गृह प्रवेश, विवाह, नींव पूजन, दीर्घकालिक निवेश, वृक्षारोपण, पदभार ग्रहण।',
    avoidForEn: 'Temporary or short-lived transitory contracts.',
    avoidForHi: 'अस्थायी अथवा त्वरित समाप्त होने वाले कार्य।'
  },
  {
    index: 4,
    name: 'Mrigashira',
    nameHi: 'मृगशिरा',
    ruler: 'Mars',
    deity: 'Soma (Moon God / Divine Nectar)',
    deityHi: 'सोम / चंद्र (अमृत व जिज्ञासा)',
    motionType: 'dvisvabhava',
    motionLabelEn: 'Mridu / Dvisvabhava (Gentle & Dual)',
    motionLabelHi: 'मृदु / द्वि-स्वभाव (कोमल व गतिशील)',
    classicalQualityEn: 'Curious, seeking, aesthetic, diplomatic and gentle.',
    classicalQualityHi: 'जिज्ञासु, अन्वेषी, सौम्य, सौंदर्य व शोधप्रिय।',
    meaningEn: 'Exploration, research, travel, wearing fine clothes, romantic courtship, friendship.',
    meaningHi: 'अन्वेषण, यात्रा, वस्त्र-आभूषण धारण, कला, प्रणय व मित्रता।',
    bestUsedForEn: 'Starting educational courses, buying luxury items, travel, meeting advisors, creative writing.',
    bestUsedForHi: 'शिक्षा आरंभ, कला-संगीत, वस्त्र-आभूषण क्रय, पर्यटन, परामर्श।',
    avoidForEn: 'Aggressive litigation, hostile confrontations.',
    avoidForHi: 'विवाद, मुकदमा व उग्र शत्रुतापूर्ण कार्य।'
  },
  {
    index: 5,
    name: 'Ardra',
    nameHi: 'आर्द्रा',
    ruler: 'Rahu',
    deity: 'Rudra (Lord of Storms & Transformation)',
    deityHi: 'रुद्र (तूफान व विनाशकारी शुद्धि)',
    motionType: 'tikshna',
    motionLabelEn: 'Tikshna / Daruna (Sharp & Dreadful)',
    motionLabelHi: 'तीक्ष्ण / दारुण (कठिन व विनाशक)',
    classicalQualityEn: 'Stormy, disruptive, deep emotional cleansing, intellectual depth.',
    classicalQualityHi: 'तूफानी, विघटनकारी, अश्रुपात के उपरांत शुद्धि, गहन शोध।',
    meaningEn: 'Overcoming massive hurdles, severing ties, technical computing, research breakthroughs.',
    meaningHi: 'बाधा निवारण, बंधन मुक्ति, तंत्र-मंत्र, आईटी/सॉफ्टवेयर कोडिंग, शोध।',
    bestUsedForEn: 'Demolishing old setups, psychological therapy, legal defenses, deep coding/AI research.',
    bestUsedForHi: 'पुराने विवाद समाप्त करना, विष-निवारण, गहन अनुसंधान, शल्य कर्म।',
    avoidForEn: 'Auspicious marriage, buying property, joyous celebrations.',
    avoidForHi: 'मांगलिक उत्सव, विवाह, गृह प्रवेश व शांति कर्म।'
  },
  {
    index: 6,
    name: 'Punarvasu',
    nameHi: 'पुनर्वसु',
    ruler: 'Jupiter',
    deity: 'Aditi (Universal Cosmic Mother)',
    deityHi: 'अदिति (देवमाता)',
    motionType: 'chara',
    motionLabelEn: 'Chara / Chala (Movable & Dynamic)',
    motionLabelHi: 'चर / चल (गतिशील व पुनरावर्ती)',
    classicalQualityEn: 'Renewal, return of light, recovery, forgiveness and benevolence.',
    classicalQualityHi: 'पुनरुद्धार, प्रकाश का पुनरागमन, आरोग्यता, क्षमा व कल्याण।',
    meaningEn: 'Restarting halted projects, returning home, healing, purchasing vehicles.',
    meaningHi: 'रुके कार्य पुनः आरंभ करना, घर लौटना, स्वास्थ्य लाभ, वाहन क्रय।',
    bestUsedForEn: 'Travel, beginning healing regimes, launching ventures for the 2nd time, buying cars, pilgrimage.',
    bestUsedForHi: 'यात्रा, वाहन क्रय, चिकित्सा, तीर्थाटन, रुके हुए प्रोजेक्ट दोबारा शुरू करना।',
    avoidForEn: 'Actions intended to never repeat again (like permanent farewells).',
    avoidForHi: 'ऐसे कार्य जिनका दोहराव न चाहते हों।'
  },
  {
    index: 7,
    name: 'Pushya',
    nameHi: 'पुष्य',
    ruler: 'Saturn',
    deity: 'Brihaspati (Guru of the Gods)',
    deityHi: 'बृहस्पति (देवगुरु)',
    motionType: 'kshipra',
    motionLabelEn: 'Kshipra / Laghu (King of Stars / Nourishing)',
    motionLabelHi: 'क्षिप्र / लघु (अमृत समान शुभ)',
    classicalQualityEn: 'The supreme nourishing star for all Vedic endeavors (except marriage).',
    classicalQualityHi: 'समस्त नक्षत्रों का राजा, परम पोषक, ज्ञान व समृद्धि प्रदायक।',
    meaningEn: 'Spiritual initiation, learning scriptures, banking, buying gold, taking office.',
    meaningHi: 'दीक्षा, विद्यारंभ, स्वर्ण क्रय, व्यापार आरंभ, आध्यात्मिक साधना।',
    bestUsedForEn: 'Starting any major noble enterprise, buying gold, opening businesses, taking oaths, mantra sadhana.',
    bestUsedForHi: 'स्वर्ण क्रय, व्यापार विस्तार, मंत्र साधना, विद्यारंभ, पदभार ग्रहण।',
    avoidForEn: 'Marriage (classical prohibition for Pushya).',
    avoidForHi: 'विवाह संस्कार (शास्त्रीय निषेध)।'
  },
  {
    index: 8,
    name: 'Ashlesha',
    nameHi: 'आश्लेषा',
    ruler: 'Mercury',
    deity: 'Sarpa / Nagas (Serpent Deities of Wisdom)',
    deityHi: 'नाग / सर्प देव (गूढ़ प्रज्ञा)',
    motionType: 'tikshna',
    motionLabelEn: 'Tikshna / Daruna (Sharp & Intricate)',
    motionLabelHi: 'तीक्ष्ण / दारुण (गूढ़ व कुटिल)',
    classicalQualityEn: 'Occult wisdom, hypnotic intuition, venom extraction, strategic defense.',
    classicalQualityHi: 'गूढ़ ज्ञान, कुंडलनी जागरण, विष निवारण, कूटनीतिक विजय।',
    meaningEn: 'Secret strategies, defensive measures, chemical sciences, occult exploration.',
    meaningHi: 'गुप्त नीतियां, रक्षात्मक कदम, रसायन विज्ञान, तंत्र व गूढ़ विद्या।',
    bestUsedForEn: 'Administering herbal venom antidotes, filing lawsuits, spiritual kundalini yoga, confidential meetings.',
    bestUsedForHi: 'शत्रु पराजय, गोपनीय योजनाएं, तंत्र साधना, रसायन व औषध शोध।',
    avoidForEn: 'Open, transparent partnerships, public festivals.',
    avoidForHi: 'पारदर्शी साझेदारी व सार्वजनिक उत्सव।'
  },
  {
    index: 9,
    name: 'Magha',
    nameHi: 'मघा',
    ruler: 'Ketu',
    deity: 'Pitris (Ancestral Spirits)',
    deityHi: 'पितृगण (पूर्वज)',
    motionType: 'ugra',
    motionLabelEn: 'Ugra / Krura (Fierce & Royal)',
    motionLabelHi: 'उग्र / क्रूर (राजकीय व पितृ प्रभाव)',
    classicalQualityEn: 'Lineage blessings, majestic authority, traditional roots, solemn rituals.',
    classicalQualityHi: 'पितृ कृपा, राजसी सत्ता, परंपरा, पितृ तर्पण व भव्यता।',
    meaningEn: 'Coronations, ancestor veneration (Shraddha), government petitions, major historic legacy.',
    meaningHi: 'पितृ पूजन/तर्पण, पदोन्नति, राजसी कार्य, पैतृक संपत्ति से जुड़े मामले।',
    bestUsedForEn: 'Ancestor rites, historical research, assuming leadership, government honors, grand ceremonies.',
    bestUsedForHi: 'पितृ तर्पण, सत्ता ग्रहण, सरकारी काम, पैतृक संपत्ति सुलझाना।',
    avoidForEn: 'Lending money, delicate sensitive discussions.',
    avoidForHi: 'उधार देना, नए ऋण लेना, अत्यंत कोमल चर्चा।'
  },
  {
    index: 10,
    name: 'Purva Phalguni',
    nameHi: 'पूर्वा फाल्गुनी',
    ruler: 'Venus',
    deity: 'Bhaga (God of Fortune & Prosperity)',
    deityHi: 'भग (सौभाग्य व समृद्धि)',
    motionType: 'ugra',
    motionLabelEn: 'Ugra (Passionate & Creative)',
    motionLabelHi: 'उग्र / काम्य (रसिक व सृजनात्मक)',
    classicalQualityEn: 'Sensory delight, artistic fertility, celebration, romance, luxurious comfort.',
    classicalQualityHi: 'आनंद, ऐश्वर्य, कलात्मक सृजन, उत्सव व प्रेम संबंध।',
    meaningEn: 'Rest and relaxation, artistic creation, romance, musical concerts, luxury purchases.',
    meaningHi: 'विश्राम, मनोरंजन, संगीत, कला, विलासिता सामग्री क्रय व प्रेम।',
    bestUsedForEn: 'Romantic engagements, purchasing perfumes/art, theatrical arts, taking vacations, social gala.',
    bestUsedForHi: 'कला-संगीत, वस्त्र-आभूषण, मनोरंजन, अवकाश यात्रा, सौंदर्य प्रसाधन।',
    avoidForEn: 'Beginning austere ascetic practices, rigorous fasting.',
    avoidForHi: 'कठोर तपस्या, गंभीर उपवास व रूखे परिश्रम वाले कार्य।'
  },
  {
    index: 11,
    name: 'Uttara Phalguni',
    nameHi: 'उत्तरा फाल्गुनी',
    ruler: 'Sun',
    deity: 'Aryaman (God of Patronage & Contracts)',
    deityHi: 'अर्यमा (संरक्षण व अनुबंध)',
    motionType: 'sthira',
    motionLabelEn: 'Sthira / Achala (Fixed & Generous)',
    motionLabelHi: 'स्थिर / अचल (सदा स्थायी)',
    classicalQualityEn: 'Enduring friendship, royal beneficence, philanthropic foundations, lasting bonds.',
    classicalQualityHi: 'सदा स्थायी, मित्रता, दान-धर्म, विवाह बंधन व दीर्घकालिक स्थायित्व।',
    meaningEn: 'Long-term contracts, legal unions, purchasing estate, public coronation, philanthropic gifts.',
    meaningHi: 'दीर्घकालीन अनुबंध, विवाह, संपत्ति क्रय, संस्था स्थापना, दान।',
    bestUsedForEn: 'Marriage, signing long covenants, founding organizations, Griha Pravesh, charity trusts.',
    bestUsedForHi: 'विवाह, अनुबंध पत्र पर हस्ताक्षर, गृह प्रवेश, ट्रस्ट/संस्था आरंभ।',
    avoidForEn: 'Filing adversarial disputes or aggressive breaks.',
    avoidForHi: 'विवाद व अलगाव उत्पन्न करने वाले कार्य।'
  },
  {
    index: 12,
    name: 'Hasta',
    nameHi: 'हस्त',
    ruler: 'Moon',
    deity: 'Savitr (The Radiant Sun God of Awakening)',
    deityHi: 'सविता / सूर्य (जागृति व शिल्प)',
    motionType: 'kshipra',
    motionLabelEn: 'Kshipra / Laghu (Swift & Dexterous)',
    motionLabelHi: 'क्षिप्र / लघु (हस्तशिल्प व द्रुत)',
    classicalQualityEn: 'Manual dexterity, healing hands, comedic wit, swift manifestation, craft.',
    classicalQualityHi: 'हाथों का कौशल, शिल्प, हास्य-विनोद, त्वरित परिणाम, व्यापार।',
    meaningEn: 'Artisanal crafts, surgery, trading, magic, writing, technical installation.',
    meaningHi: 'शिल्पकला, हस्तशिल्प, वाणिज्य, लेखन, शल्य चिकित्सा, व्यापार।',
    bestUsedForEn: 'Commercial trading, learning handicrafts, starting technical projects, medical procedures, witty negotiations.',
    bestUsedForHi: 'व्यापार/दुकान खोलना, हस्तशिल्प सीखना, शल्य चिकित्सा, वाणिज्य।',
    avoidForEn: 'Heavy sedentary inactivity.',
    avoidForHi: 'आलस्य व लंबे ठहराव वाले कार्य।'
  },
  {
    index: 13,
    name: 'Chitra',
    nameHi: 'चित्रा',
    ruler: 'Mars',
    deity: 'Tvashtar / Vishwakarma (Cosmic Architect)',
    deityHi: 'त्वष्टा / विश्वकर्मा (सृष्टि के दिव्य शिल्पी)',
    motionType: 'dvisvabhava',
    motionLabelEn: 'Mridu / Dvisvabhava (Gentle & Artistic)',
    motionLabelHi: 'मृदु / द्वि-स्वभाव (अद्भुत शिल्पी)',
    classicalQualityEn: 'Architectural genius, visual splendor, gemology, intricate geometry.',
    classicalQualityHi: 'वास्तु कला, रूप-सौंदर्य, रत्न विज्ञान, डिजाइनिंग व दृश्य कला।',
    meaningEn: 'Interior decoration, architectural drafting, jewelry designing, wearing gems, fine arts.',
    meaningHi: 'वास्तु, घर की सजावट, आभूषण निर्माण, रत्न धारण, ग्राफिक डिजाइन।',
    bestUsedForEn: 'Designing buildings, wearing new gemstones, photography, fashion shows, spiritual sculpture.',
    bestUsedForHi: 'मकान का नक्शा/डिजाइन, रत्न धारण, फैशन, मूर्तिकला, फोटोग्राफी।',
    avoidForEn: 'Crude or routine uninspired labor.',
    avoidForHi: 'नीरस व कलाहीन कार्य।'
  },
  {
    index: 14,
    name: 'Swati',
    nameHi: 'स्वाती',
    ruler: 'Rahu',
    deity: 'Vayu (God of Wind & Prana)',
    deityHi: 'वायु देव (प्राण व पवन)',
    motionType: 'chara',
    motionLabelEn: 'Chara / Chala (Movable & Free)',
    motionLabelHi: 'चर / चल (स्वतंत्र व गतिशील)',
    classicalQualityEn: 'Independence, flexibility, travel, commerce, education, music, pranayama.',
    classicalQualityHi: 'स्वाधीनता, लचीलापन, वायु तत्व, व्यापार, संगीत व प्राणायाम।',
    meaningEn: 'Learning music, acquiring transport, trade across borders, breathing sadhana.',
    meaningHi: 'संगीत सीखना, वाहन क्रय, विदेश व्यापार, प्राणायाम, शेयर व्यापार।',
    bestUsedForEn: 'Purchasing cars/aeroplanes, starting foreign trade, buying seeds, playing musical instruments, travel.',
    bestUsedForHi: 'वाहन क्रय, आयात-निर्यात, संगीत वादन, कृषि बीज बोना, यात्रा।',
    avoidForEn: 'Actions requiring heavy grounded immobility.',
    avoidForHi: 'भारी ठहराव व जड़ता वाले कार्य।'
  },
  {
    index: 15,
    name: 'Vishakha',
    nameHi: 'विशाखा',
    ruler: 'Jupiter',
    deity: 'Indragni (Indra + Agni: Triumph & Zeal)',
    deityHi: 'इन्द्राग्नि (इन्द्र व अग्नि - विजय)',
    motionType: 'dvisvabhava',
    motionLabelEn: 'Misra / Dvisvabhava (Goal-Oriented Triumph)',
    motionLabelHi: 'मिश्र / द्वि-स्वभाव (विजय व लक्ष्य)',
    classicalQualityEn: 'Laser focus, burning determination, competitive success, victorious celebrations.',
    classicalQualityHi: 'दृढ़ संकल्प, लक्ष्य भेदन, प्रतियोगी परीक्षा में सफलता, विजयोत्सव।',
    meaningEn: 'Competitive examinations, launching political campaigns, celebrations, reaching summits.',
    meaningHi: 'प्रतियोगी परीक्षाएं, चुनाव/अभियान आरंभ, खेलकूद, लक्ष्य प्राप्ति।',
    bestUsedForEn: 'Taking exams, athletic tournaments, bold leadership bids, victory celebrations, intense focus.',
    bestUsedForHi: 'परीक्षाएं देना, खेल प्रतियोगिता, नेतृत्व संभालना, उत्सव।',
    avoidForEn: 'Submissive compromises or sensitive peace talks.',
    avoidForHi: 'दब्बू समझौते व मौन आत्मसमर्पण।'
  },
  {
    index: 16,
    name: 'Anuradha',
    nameHi: 'अनुराधा',
    ruler: 'Saturn',
    deity: 'Mitra (God of Divine Friendship & Light)',
    deityHi: 'मित्र देव (मैत्री व सहयोग)',
    motionType: 'dvisvabhava',
    motionLabelEn: 'Mridu / Dvisvabhava (Gentle & Friendly)',
    motionLabelHi: 'मृदु / द्वि-स्वभाव (मैत्रीपूर्ण व शुभ)',
    classicalQualityEn: 'Devotion, diplomatic success, overseas travel, occult loyalty, friendship.',
    classicalQualityHi: 'भक्ति, मित्रता, विदेश यात्रा, कूटनीति, संगठन व सौहार्द।',
    meaningEn: 'Collaborative alliances, devotional bhakti, overseas journeys, social initiatives.',
    meaningHi: 'मित्रता, साझेदारी, विदेश यात्रा, भक्ति संगीत, धार्मिक अनुष्ठान।',
    bestUsedForEn: 'Forming business partnerships, international travel, singing hymns/bhajans, mediation, making friends.',
    bestUsedForHi: 'साझेदारी अनुबंध, विदेश यात्रा, भजन-कीर्तन, सुलह-समझौता, मित्रता।',
    avoidForEn: 'Direct military aggression, violent confrontation.',
    avoidForHi: 'सीधे उग्र हमले व हिंसक टकराव।'
  },
  {
    index: 17,
    name: 'Jyeshtha',
    nameHi: 'ज्येष्ठा',
    ruler: 'Mercury',
    deity: 'Indra (King of the Gods)',
    deityHi: 'इन्द्र (देवराज - ज्येष्ठता व शक्ति)',
    motionType: 'tikshna',
    motionLabelEn: 'Tikshna / Daruna (Sharp & Authoritative)',
    motionLabelHi: 'तीक्ष्ण / दारुण (वरिष्ठ व तीक्ष्ण)',
    classicalQualityEn: 'Eldership, executive authority, talismanic protection, administrative defense.',
    classicalQualityHi: 'वरिष्ठता, प्रशासनिक शक्ति, ताबीज/कवच धारण, पराक्रम।',
    meaningEn: 'Protecting the vulnerable, assuming executive control, military command, overcoming rivals.',
    meaningHi: 'प्रशासनिक कड़ाई, सुरक्षा, उच्च पदभार, शत्रु दमन, रक्षात्मक उपाय।',
    bestUsedForEn: 'Wearing protective amulets, asserting seniority, administrative enforcement, covert investigations.',
    bestUsedForHi: 'कवच/ताबीज धारण, प्रशासनिक आदेश, जांच-पड़ताल, वरिष्ठता सिद्ध करना।',
    avoidForEn: 'Marriage, gentle artistic romance, lighthearted travel.',
    avoidForHi: 'विवाह, कोमल रोमान्स व हल्की-फुल्की यात्राएं।'
  },
  {
    index: 18,
    name: 'Mula',
    nameHi: 'मूल',
    ruler: 'Ketu',
    deity: 'Nirriti (Goddess of Dissolution & Roots)',
    deityHi: 'निर्रिति (विनाश व मूल शुद्धि)',
    motionType: 'tikshna',
    motionLabelEn: 'Tikshna / Daruna (Sharp & Root-Striking)',
    motionLabelHi: 'तीक्ष्ण / दारुण (जड़ से उखाड़ना)',
    classicalQualityEn: 'Uprooting the false, deep botanical/herbal medicine, investigation of root causes.',
    classicalQualityHi: 'जड़ से समाधान, औषध मूल संचय, अनुसंधान, भ्रम का विनाश।',
    meaningEn: 'Root cause analysis, herbal foraging, breaking toxic habits, agricultural rooting.',
    meaningHi: 'समस्या की जड़ तक पहुंचना, जड़ी-बूटी संचय, बुरी आदतें त्यागना, शोध।',
    bestUsedForEn: 'Planting roots/trees, investigating root causes, medical root harvesting, terminating harmful ties.',
    bestUsedForHi: 'जड़ वाले पौधे रोपना, जड़ी-बूटी सेवन, बुरी लत छोड़ना, गहन खोज।',
    avoidForEn: 'Borrowing wealth, light casual festivities, marriage.',
    avoidForHi: 'उधार लेना, विवाह व हल्के-फुल्के मांगलिक कार्य।'
  },
  {
    index: 19,
    name: 'Purva Ashadha',
    nameHi: 'पूर्वाषाढ़ा',
    ruler: 'Venus',
    deity: 'Apas (Cosmic Water Goddesses)',
    deityHi: 'आपः (जल देवियां - अपराजेय शक्ति)',
    motionType: 'ugra',
    motionLabelEn: 'Ugra / Krura (Invincible & Fluid)',
    motionLabelHi: 'उग्र / क्रूर (अपराजेय व जल तत्व)',
    classicalQualityEn: 'Invincible resolve, maritime commerce, spiritual purification, poetic eloquence.',
    classicalQualityHi: 'अजेय संकल्प, जल व नौकायन कार्य, काव्य, आध्यात्मिक शुद्धि।',
    meaningEn: 'Water bodies, wells, declarations of victory, artistic rhetoric, emotional conviction.',
    meaningHi: 'जल शोधन, कुआं/तालाब निर्माण, विजय घोषणा, काव्य पाठ, प्रेरणा।',
    bestUsedForEn: 'Digging wells/borewells, maritime trade, artistic debates, inspiring public speeches, water sports.',
    bestUsedForHi: 'बोरवेल/कुआं खनन, जल व्यापार, भाषण, कलात्मक संवाद, जल क्रीड़ा।',
    avoidForEn: 'Peaceful long-term domestic domesticity.',
    avoidForHi: 'स्थिर घरेलू शांति के समझौते।'
  },
  {
    index: 20,
    name: 'Uttara Ashadha',
    nameHi: 'उत्तराषाढ़ा',
    ruler: 'Sun',
    deity: 'Vishvadevas (All-Pervading Cosmic Virtues)',
    deityHi: 'विश्वेदेव (सद्गुण व परम विजय)',
    motionType: 'sthira',
    motionLabelEn: 'Sthira / Achala (Fixed & Victorious)',
    motionLabelHi: 'स्थिर / अचल (चिरस्थायी विजय)',
    classicalQualityEn: 'Unshakeable victory, universal harmony, permanent foundations, righteousness.',
    classicalQualityHi: 'अटल विजय, न्याय, स्थायी आधार, गृह प्रवेश व अखंड प्रतिष्ठा।',
    meaningEn: 'Foundational architecture, coronations, constitutional pacts, lasting enterprises.',
    meaningHi: 'नींव पूजन, गृह प्रवेश, कानूनी संधि, व्यापार स्थापना, शपथ ग्रहण।',
    bestUsedForEn: 'Griha Pravesh, laying cornerstones, signing permanent treaties, appointing executives, marriage.',
    bestUsedForHi: 'गृह प्रवेश, नींव पूजन, स्थायी अनुबंध, पदभार ग्रहण, विवाह।',
    avoidForEn: 'Quick impulsive short-lived actions.',
    avoidForHi: 'क्षणिक व जल्दबाजी वाले कार्य।'
  },
  {
    index: 21,
    name: 'Shravana',
    nameHi: 'श्रवण',
    ruler: 'Moon',
    deity: 'Vishnu (Preserver of the Universe)',
    deityHi: 'भगवान विष्णु (जगत के पालनहार)',
    motionType: 'chara',
    motionLabelEn: 'Chara / Chala (Movable & Learned)',
    motionLabelHi: 'चर / चल (ज्ञान श्रवण व गति)',
    classicalQualityEn: 'Deep listening, sacred oral tradition, high learning, travel, divine preservation.',
    classicalQualityHi: 'श्रवण, वेदों का ज्ञान, उच्च शिक्षा, यात्रा, परोपकार व विष्णु कृपा।',
    meaningEn: 'Higher education, podcasts, counseling, medical therapies, travel, philosophy.',
    meaningHi: 'उच्च शिक्षा, परामर्श, संगीत श्रवण, यात्रा, औषधोपचार, सत्संग।',
    bestUsedForEn: 'Starting academic studies, counseling, buying vehicles, beginning pilgrimages, meditation, music.',
    bestUsedForHi: 'विद्यारंभ, परामर्श, वाहन क्रय, तीर्थ यात्रा, ध्यान व संगीत।',
    avoidForEn: 'Hostile litigation, aggressive demolition.',
    avoidForHi: 'शत्रुतापूर्ण विवाद व विध्वंस।'
  },
  {
    index: 22,
    name: 'Dhanishta',
    nameHi: 'धनिष्ठा',
    ruler: 'Mars',
    deity: 'Ashta Vasus (Eight Gods of Abundance & Rhythm)',
    deityHi: 'अष्ट वसु (आठ समृद्धि देव)',
    motionType: 'chara',
    motionLabelEn: 'Chara / Chala (Movable & Symphonic)',
    motionLabelHi: 'चर / चल (समृद्धि व संगीत)',
    classicalQualityEn: 'Rhythmic abundance, wealth, music, dance, real estate development, martial valor.',
    classicalQualityHi: 'ताल-लय, प्रचुर धन, संगीत-नृत्य, भूमि विकास व शौर्य।',
    meaningEn: 'Real estate, acquiring land, musical performances, opening financial vaults, sports.',
    meaningHi: 'जमीन-जायदाद, संगीत कार्यक्रम, बैंक खाता खोलना, खेलकूद, व्यापार।',
    bestUsedForEn: 'Buying real estate, learning drums/music, opening bank accounts, inaugurating stadiums, sports.',
    bestUsedForHi: 'भूमि क्रय, संगीत सीखना, वित्तीय निवेश, खेलकूद आरंभ, व्यापार।',
    avoidForEn: 'Southern travel (classical limitation), marriage.',
    avoidForHi: 'दक्षिण दिशा की यात्रा, विवाह (कुछ परंपराओं में)।'
  },
  {
    index: 23,
    name: 'Shatabhisha',
    nameHi: 'शतभिषा',
    ruler: 'Rahu',
    deity: 'Varuna (God of Cosmic Oceans & Mystical Veil)',
    deityHi: 'वरुण देव (समुद्र व रहस्यमय औषधि)',
    motionType: 'chara',
    motionLabelEn: 'Chara / Chala (Movable & 100 Healers)',
    motionLabelHi: 'चर / चल (शत-वैद्य व रहस्य)',
    classicalQualityEn: 'One hundred divine physicians, secret remedies, astronomical research, aviation, mysticism.',
    classicalQualityHi: 'सौ वैद्यों की शक्ति, रहस्यमयी औषधियां, खगोल विज्ञान, विमानन व योग।',
    meaningEn: 'Medical treatments, oceanic voyages, technology, astronomy, esoteric meditation.',
    meaningHi: 'चिकित्सा आरंभ, समुद्री यात्रा, आईटी/इंटरनेट तकनीक, ध्यान, ज्योतिष।',
    bestUsedForEn: 'Starting complex medical treatments, acquiring software, astronomy/space research, hydrotherapy.',
    bestUsedForHi: 'गंभीर रोग की चिकित्सा, सॉफ्टवेयर/नेटवर्क, खगोल शोध, जल चिकित्सा।',
    avoidForEn: 'Public open ceremonies requiring traditional simplicity.',
    avoidForHi: 'साधारण पारंपरिक उत्सव।'
  },
  {
    index: 24,
    name: 'Purva Bhadrapada',
    nameHi: 'पूर्वाभाद्रपद',
    ruler: 'Jupiter',
    deity: 'Aja Ekapada (The Cosmic One-Footed Serpent of Fire)',
    deityHi: 'अज एकपाद (दिव्य अग्नि शक्ति)',
    motionType: 'ugra',
    motionLabelEn: 'Ugra / Krura (Ascetic & Fierce Fire)',
    motionLabelHi: 'उग्र / क्रूर (तपस्या व उग्र तेज)',
    classicalQualityEn: 'Ascetic discipline, spiritual awakening, fierce truth, cutting material illusions.',
    classicalQualityHi: 'कठोर तप, वैराग्य, आध्यात्मिक जागरण, माया का विच्छेदन।',
    meaningEn: 'Tantric meditation, deep research, bold financial restructuring, renunciation.',
    meaningHi: 'गूढ़ ध्यान, वित्तीय पुनर्गठन, कठोर अनुसंधान, व्यसन त्याग।',
    bestUsedForEn: 'Intense meditation, bold corrective operations, scientific breakthroughs, cutting debt.',
    bestUsedForHi: 'गहन साधना, शोध कार्य, ऋण मुक्ति की योजना, कठोर निर्णय।',
    avoidForEn: 'Auspicious celebratory marriages, light parties.',
    avoidForHi: 'हर्षोल्लास वाले मांगलिक कार्य व विवाह।'
  },
  {
    index: 25,
    name: 'Uttara Bhadrapada',
    nameHi: 'उत्तराभाद्रपद',
    ruler: 'Saturn',
    deity: 'Ahirbudhnya (Serpent of the Ocean Depths / Kundalini)',
    deityHi: 'अहिर्बुध्न्य (समुद्र तल व कुण्डलिनी)',
    motionType: 'sthira',
    motionLabelEn: 'Sthira / Achala (Fixed & Enlightened Depth)',
    motionLabelHi: 'स्थिर / अचल (गहन शांति व स्थिरता)',
    classicalQualityEn: 'Profound peace, enduring wisdom, benevolent foundation, charity, moksha.',
    classicalQualityHi: 'परम शांति, अगाध प्रज्ञा, परोपकार, मोक्ष व दीर्घकालीन स्थायित्व।',
    meaningEn: 'Permanent foundations, spiritual retirement, monastery endowment, investments, charity.',
    meaningHi: 'स्थायी नींव, आश्रम/मंदिर निर्माण, दीर्घ निवेश, दान-पुण्य, विवाह।',
    bestUsedForEn: 'Griha Pravesh, marriage, creating trusts, meditation centers, long-term wealth assets.',
    bestUsedForHi: 'गृह प्रवेश, विवाह, ट्रस्ट स्थापना, ध्यान केंद्र आरंभ, स्थायी निवेश।',
    avoidForEn: 'Impulsive gambling or rapid short speculation.',
    avoidForHi: 'सट्टेबाजी व जल्दबाजी वाले अल्पकालिक निर्णय।'
  },
  {
    index: 26,
    name: 'Revati',
    nameHi: 'रेवती',
    ruler: 'Mercury',
    deity: 'Pushan (God of Nourishment, Safe Journeys & Herds)',
    deityHi: 'पूषा (पोषण, सुरक्षित यात्रा व पशुपालन)',
    motionType: 'dvisvabhava',
    motionLabelEn: 'Mridu / Dvisvabhava (Gentle & Nourishing Journey)',
    motionLabelHi: 'मृदु / द्वि-स्वभाव (अत्यंत कोमल व कल्याणकारी)',
    classicalQualityEn: 'Safe travel, supreme sweetness, wealth, cattle, music, arts, joyous celebration.',
    classicalQualityHi: 'सुरक्षित यात्रा, मधुरता, धन-समृद्धि, कला, संगीत व मांगलिक आनंद।',
    meaningEn: 'Safe journeys, gem purchases, learning fine arts, romantic weddings, spiritual culmination.',
    meaningHi: 'सुरक्षित यात्रा, रत्न क्रय, ललित कला, विवाह संस्कार, व्यापार आरंभ।',
    bestUsedForEn: 'Marriage, starting long journeys, buying jewelry/gems, learning music/dance, commercial launch.',
    bestUsedForHi: 'विवाह, लंबी यात्रा आरंभ, आभूषण क्रय, संगीत-नृत्य, नया व्यापार।',
    avoidForEn: 'Ruthless hostility or cruel disciplinary action.',
    avoidForHi: 'क्रूर अथवा कठोर दंडात्मक कार्य।'
  }
];

// Special Sensitive Nakshatras (Parashari / Classical Jyotish sensitive points)
export interface SpecialSensitiveNakshatraInfo {
  offsetFromJanma: number; // 0-indexed offset (0 to 26)
  starNumber: number; // 1 to 27
  roleId: 'janma' | 'karma' | 'samudayika' | 'sanghatika' | 'vinashika' | 'manasa' | 'rajya' | 'abhisheka';
  titleEn: string;
  titleHi: string;
  badgeLabelEn: string;
  badgeLabelHi: string;
  meaningEn: string;
  meaningHi: string;
  significanceEn: string;
  significanceHi: string;
  color: 'amber' | 'indigo' | 'rose' | 'emerald' | 'purple' | 'blue';
}

export const SPECIAL_SENSITIVE_NAKSHATRAS: SpecialSensitiveNakshatraInfo[] = [
  {
    offsetFromJanma: 0,
    starNumber: 1,
    roleId: 'janma',
    titleEn: 'Janma Nakshatra (Deha / Physical Vitality)',
    titleHi: 'जन्म नक्षत्र (देह व शारीरिक प्राण)',
    badgeLabelEn: 'Janma (Body)',
    badgeLabelHi: 'जन्म (देह)',
    meaningEn: 'Governs physical vitality, self-identity, temperament, and vital life-force.',
    meaningHi: 'शारीरिक स्वास्थ्य, जीवन शक्ति, स्वभाव एवं व्यक्तिगत पहचान का मुख्य आधार।',
    significanceEn: 'Transits here require grounding, adequate rest, and avoiding severe physical exhaustion.',
    significanceHi: 'इस नक्षत्र पर गोचर के समय स्वास्थ्य की देखभाल, पर्याप्त विश्राम व आत्म-चिंतन आवश्यक है।',
    color: 'amber'
  },
  {
    offsetFromJanma: 9,
    starNumber: 10,
    roleId: 'karma',
    titleEn: 'Karma Nakshatra (Professional Actions & Legacy)',
    titleHi: 'कर्म नक्षत्र (कार्यक्षेत्र व आजीविका)',
    badgeLabelEn: 'Karma (Career)',
    badgeLabelHi: 'कर्म (आजीविका)',
    meaningEn: 'The 10th star from Moon; strongly rules career achievements, professional initiatives, and public deeds.',
    meaningHi: 'चंद्र से १०वां नक्षत्र; आजीविका, व्यवसाय, पदोन्नति एवं कर्मक्षेत्र में किए गए प्रयासों का प्रतीक।',
    significanceEn: 'Benefic transits over Karma Nakshatra bring career milestones, recognition, and successful projects.',
    significanceHi: 'इस नक्षत्र पर शुभ गोचर करियर में पदोन्नति, नई जिम्मेदारियां व व्यावसायिक सफलता प्रदान करता है।',
    color: 'indigo'
  },
  {
    offsetFromJanma: 15,
    starNumber: 16,
    roleId: 'sanghatika',
    titleEn: 'Sanghatika Nakshatra (Alliances, Partnerships & Debts)',
    titleHi: 'सांघातिक नक्षत्र (साझेदारी, ऋण व अनुबंध)',
    badgeLabelEn: 'Sanghatika (Alliances)',
    badgeLabelHi: 'सांघातिक (साझेदारी)',
    meaningEn: 'The 16th star from Moon; governs close partnerships, joint liabilities, group contracts, and covenants.',
    meaningHi: 'चंद्र से १६वां नक्षत्र; व्यापारिक साझेदारी, संयुक्त अनुबंध, ऋण व सामूहिक समझौतों का संचालक।',
    significanceEn: 'Requires extra scrutiny in business contracts and avoiding impulsive legal or financial obligations.',
    significanceHi: 'साझेदारी समझौतों, वित्तीय लेनदेन व अनुबंधों में विशेष सतर्कता बरतने की आवश्यकता होती है।',
    color: 'blue'
  },
  {
    offsetFromJanma: 17,
    starNumber: 18,
    roleId: 'samudayika',
    titleEn: 'Samudayika Nakshatra (Community, Public Impact & Reputation)',
    titleHi: 'सामुदायिक नक्षत्र (सामाजिक प्रतिष्ठा व जनसंपर्क)',
    badgeLabelEn: 'Samudayika (Public)',
    badgeLabelHi: 'सामुदायिक (समाज)',
    meaningEn: 'The 18th star from Moon; reflects collective karma, social relations, public reputation, and harmony with crowds.',
    meaningHi: 'चंद्र से १८वां नक्षत्र; सामूहिक कर्म, सामाजिक संबंध, जनमानस में प्रतिष्ठा व सार्वजनिक प्रभाव का सूचक।',
    significanceEn: 'Auspicious for mass communication, launching public products, organizing community events, and social reach.',
    significanceHi: 'सार्वजनिक आयोजनों, जनसंपर्क, सामुदायिक कार्यों व बड़े स्तर पर उत्पाद प्रस्तुत करने हेतु उत्तम।',
    color: 'purple'
  },
  {
    offsetFromJanma: 22,
    starNumber: 23,
    roleId: 'vinashika',
    titleEn: 'Vainashika / Vinashika Nakshatra (Vulnerability & Destabilization)',
    titleHi: 'वैनाषिक / विनाशी नक्षत्र (संवेदनशील व जोखिम बिंदु)',
    badgeLabelEn: 'Vinashika (Caution)',
    badgeLabelHi: 'वैनाषिक (सतर्कता)',
    meaningEn: 'The 23rd star from Moon; represents critical vulnerability, latent challenges, and areas requiring utmost caution.',
    meaningHi: 'चंद्र से २३वां नक्षत्र; अत्यंत संवेदनशील व नाज़ुक बिंदु, जहाँ जल्दबाजी में लिए गए निर्णय नुकसान दे सकते हैं।',
    significanceEn: 'Avoid signing high-stakes deals, surgeries, or starting major conflicts during malefic transit here.',
    significanceHi: 'इस नक्षत्र पर पाप ग्रहों के गोचर समय जोखिम भरे सौदों, शल्य क्रिया व बड़े विवादों से बचें।',
    color: 'rose'
  },
  {
    offsetFromJanma: 24,
    starNumber: 25,
    roleId: 'manasa',
    titleEn: 'Manasa Nakshatra (Subconscious Mind & Mental Harmony)',
    titleHi: 'मानस नक्षत्र (अंतर्मन व मानसिक शांति)',
    badgeLabelEn: 'Manasa (Mind)',
    badgeLabelHi: 'मानस (मन)',
    meaningEn: 'The 25th star from Moon; governs deep subconscious states, psychological poise, dreams, and emotional tranquility.',
    meaningHi: 'चंद्र से २५वां नक्षत्र; अंतर्मन, मानसिक एकाग्रता, भावनात्मक संतुलन व आंतरिक शांति का संचालक।',
    significanceEn: 'Benefic transit here induces creative clarity and meditation bliss; malefic transits require mindfulness.',
    significanceHi: 'यहाँ शुभ गोचर से मानसिक शांति, ध्यान व रचनात्मक विचार खिलते हैं; मानसिक तनाव से बचना चाहिए।',
    color: 'indigo'
  },
  {
    offsetFromJanma: 25,
    starNumber: 26,
    roleId: 'rajya',
    titleEn: 'Rajya Nakshatra (Government, Authority & Official Status)',
    titleHi: 'राज्य नक्षत्र (सत्ता, प्रतिष्ठा व प्रशासनिक प्रभाव)',
    badgeLabelEn: 'Rajya (Authority)',
    badgeLabelHi: 'राज्य (सत्ता)',
    meaningEn: 'The 26th star from Moon; governs royal favor, promotions, bureaucratic approvals, and public prestige.',
    meaningHi: 'चंद्र से २६वां नक्षत्र; शासन-प्रशासन, उच्च पद, सरकारी अनुमतियां व अधिकारिक सम्मान से संबंधित।',
    significanceEn: 'Optimal for meeting officials, applying for government contracts, licensing, and assuming titles.',
    significanceHi: 'प्रशासनिक कार्यों, सरकारी निविदाओं, लाइसेंस आवेदन व उच्चाधिकारियों से भेंट के लिए सर्वोत्तम।',
    color: 'emerald'
  },
  {
    offsetFromJanma: 26,
    starNumber: 27,
    roleId: 'abhisheka',
    titleEn: 'Abhisheka Nakshatra (Coronation, Culmination & Mastery)',
    titleHi: 'अभिषेक नक्षत्र (राज्याभिषेक, उत्थान व सिद्धि)',
    badgeLabelEn: 'Abhisheka (Coronation)',
    badgeLabelHi: 'अभिषेक (उत्थान)',
    meaningEn: 'The 27th star from Moon; signifies the culmination of effort, coronation, major milestones, and divine grace.',
    meaningHi: 'चंद्र से २७वां नक्षत्र; महान सिद्धि, राज्याभिषेक, दीर्घ प्रयासों की पूर्णाहुति व सर्वोच्च पद प्रतिष्ठा।',
    significanceEn: 'Magnificent for celebrating major triumphs, inaugurations, crowning achievements, and sacred ceremonies.',
    significanceHi: 'भव्य उद्घाटन, बड़े उत्सवों, पदभार ग्रहण व जीवन के ऐतिहासिक पड़ावों के लिए अत्यंत शुभ।',
    color: 'purple'
  }
];

export function getSpecialSensitiveRole(janmaIndex: number, targetNakIndex: number): SpecialSensitiveNakshatraInfo | undefined {
  const offset = (targetNakIndex - janmaIndex + 27) % 27;
  return SPECIAL_SENSITIVE_NAKSHATRAS.find((s) => s.offsetFromJanma === offset);
}
